import { useEffect, useRef, useState } from "react";
import { Avatar, StickyPageHeader } from "../../../design-v2";
import SettingsDrawer from "../../settings/SettingsDrawer";
import { getUserSettings } from "../../../services/api";

/*
 * Top-level app header, the Home / Program pattern: avatar left, the page
 * title centred (Program's title treatment), notifications and settings on
 * the right (Home's actions). The avatar is resolved on its own request that
 * never gates Insights; the neutral placeholder stays until it answers.
 */

function BellIcon() {
  return (
    <svg className="lz-insights-header__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9.5a6 6 0 0 1 12 0c0 3.9 1.1 5.5 1.9 6.4H4.1C4.9 15 6 13.4 6 9.5Z" />
      <path d="M9.9 19.2a2.2 2.2 0 0 0 4.2 0" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg className="lz-insights-header__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.25" />
      <circle cx="12" cy="12" r="7.25" />
      <path d="M12 2.6v2.1M12 19.3v2.1M21.4 12h-2.1M4.7 12H2.6M18.65 5.35l-1.48 1.48M6.83 17.17l-1.48 1.48M18.65 18.65l-1.48-1.48M6.83 6.83 5.35 5.35" />
    </svg>
  );
}

export default function InsightsHeader({ title = "Insights" }) {
  const [profileAvatar, setProfileAvatar] = useState({ url: null, sex: null });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsTriggerRef = useRef(null);

  function closeSettings() {
    setIsSettingsOpen(false);
    settingsTriggerRef.current?.focus();
  }

  useEffect(() => {
    let isMounted = true;
    getUserSettings()
      .then((response) => {
        if (!isMounted) return;
        setProfileAvatar({
          url: response?.account?.profile?.avatar?.url || null,
          sex: response?.account?.profile?.sex || null,
        });
      })
      .catch(() => {
        // Non-blocking: the placeholder avatar stays.
      });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <StickyPageHeader className="lz-insights-header">
        <Avatar
          src={profileAvatar.url}
          sex={profileAvatar.sex}
          size={40}
          className="lz-insights-header__avatar"
          data-testid="insights-profile-avatar"
        />
        <h1 className="lz-insights-header__title">{title}</h1>
        <div className="lz-insights-header__actions">
          <button type="button" className="lz-insights-header__icon-button" aria-label="Notifications">
            <BellIcon />
          </button>
          <button
            ref={settingsTriggerRef}
            type="button"
            className="lz-insights-header__icon-button"
            aria-label="Settings"
            onClick={() => setIsSettingsOpen(true)}
          >
            <GearIcon />
          </button>
        </div>
      </StickyPageHeader>
      <SettingsDrawer isOpen={isSettingsOpen} onClose={closeSettings} />
    </>
  );
}
